import React, { ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import notification from "antd/es/notification";
import Space from "antd/es/space";
import { camelCase } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";

import { EntityTypesSupportedColumnSelection } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { getDefaultColumns, isCurrentUserManager, isCurrentUserOwner } from "@app/store/iam";
import { updateDefaultColumns } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";

const messages = defineMessages({
  resetToDefault: {
    id: "component.columnActions.resetToDefault",
    defaultMessage: "Reset to default",
    description: "Reset to default menu item in the Columns Menu",
  },
  resetToDefaultDisabledTooltip: {
    id: "component.columnActions.columnsActions.disabledTooltip",
    defaultMessage: "Columns already set to default.",
    description: "Reset to default disabled menu item in the Columns Menu tooltip",
  },
  resetToDefaultTooltip: {
    id: "component.columnActions.resetToDefault.tooltip",
    defaultMessage:
      "This resets column view for {entityType, select, accounts {Companies} contacts {People} deals {Deals} crmActivities {Activities} other {Records}} only.",
    description: "Reset to default menu item in the Columns Menu tooltip",
  },
  setAsOrgDefault: {
    id: "component.columnActions.setAsOrgDefault",
    defaultMessage: "Set as org default",
    description: "Set as org default menu item in the Columns Menu",
  },
  setAsOrgDefaultConfirmation: {
    id: "component.columnActions.setAsOrgDefault.confirmation",
    defaultMessage:
      "Selected columns will be the default for exports and list views <b>for the entire org</b>. Continue?",
    description: "Popconfirm title for the SetAsOrgDefault menu item in the Columns Menu",
  },
  setAsOrgDefaultConfirmationOk: {
    id: "component.columnActions.setAsOrgDefault.confirmation.ok",
    defaultMessage: "Continue",
    description:
      "Ok button title in the Popconfirm for the SetAsOrgDefault menu item in the Columns Menu",
  },
  successfulMessage: {
    id: "component.columnActions.resetToDefault.successfulMessage",
    defaultMessage: "Columns reset to default.",
    description: "Reset to default successful notification message",
  },
});

export interface ColumnActionsProps {
  className?: string;
  columns: ColumnModel<IField>;
  entityType: EntityTypesSupportedColumnSelection;
  onUpdate?: (visibilities: ColumnVisibility[]) => void;
}

const ColumnActions = ({
  canSetDefault,
  className,
  columns,
  defaultColumns,
  entityType,
  onUpdate,
  onUpdateDefaultColumns,
}: ColumnActionsProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const entityTypeDefaultColumns = useMemo(
    () =>
      new Set(
        (defaultColumns as Record<EntityTypesSupportedColumnSelection, string[] | undefined>)[
          entityType
        ]
      ),
    [defaultColumns, entityType]
  );

  const resetToDefaultDisabled = useMemo(() => {
    return (
      !entityTypeDefaultColumns.size ||
      !columns.some(
        (column) => column.visible !== entityTypeDefaultColumns.has(column.field.platformName)
      )
    );
  }, [columns, entityTypeDefaultColumns]);

  const handleConfirm = useCallback(() => {
    const updatedDefaultColumns = {
      ...(defaultColumns || {}),
      [entityType]: columns.filter(({ visible }) => visible).map(({ field }) => field.platformName),
    };

    onUpdateDefaultColumns({
      callback: () => {
        showSaveSettingSuccessfulMessage(intl);
      },
      defaultColumns: updatedDefaultColumns,
    });
  }, [columns, defaultColumns, entityType, intl, onUpdateDefaultColumns]);

  const handleResetToDefault = useCallback(() => {
    if (!onUpdate || !entityTypeDefaultColumns.size) {
      return;
    }

    onUpdate(
      columns.map((column) => ({
        field: column.field,
        visible: entityTypeDefaultColumns.has(column.field.platformName),
      }))
    );

    notification.success({
      message: intl.formatMessage(messages.successfulMessage),
    });
  }, [intl, columns, entityTypeDefaultColumns, onUpdate]);

  return (
    <Space className={className} size="small">
      {canSetDefault ? (
        <Popconfirm
          description={intl.formatMessage(messages.setAsOrgDefaultConfirmation, {
            b: (text: ReactNode) => <b>{text}</b>,
          })}
          okText={intl.formatMessage(messages.setAsOrgDefaultConfirmationOk)}
          onConfirm={handleConfirm}
          zIndex={1051}
        >
          <span>
            <Button leftIcon={<FontAwesomeIcon icon={faStar} />} type="primary">
              {intl.formatMessage(messages.setAsOrgDefault)}
            </Button>
          </span>
        </Popconfirm>
      ) : null}
      <Tooltip
        placement="bottom"
        title={
          resetToDefaultDisabled
            ? intl.formatMessage(messages.resetToDefaultDisabledTooltip, {
                entityType: camelCase(entityType),
              })
            : intl.formatMessage(messages.resetToDefaultTooltip, {
                entityType: camelCase(entityType),
              })
        }
      >
        <span>
          <Button disabled={resetToDefaultDisabled} onClick={handleResetToDefault} type="filled">
            {intl.formatMessage(messages.resetToDefault)}
          </Button>
        </span>
      </Tooltip>
    </Space>
  );
};

const mapStateToProps = (state: RootState) => ({
  canSetDefault: isCurrentUserOwner(state) || isCurrentUserManager(state),
  defaultColumns: getDefaultColumns(state),
});

const mapDispatchToProps = {
  onUpdateDefaultColumns: updateDefaultColumns,
};

export default connect(mapStateToProps, mapDispatchToProps)(ColumnActions);
